// ShiftArchiveView.js
// Staff-only archive of all past shifts.
// Expand any shift to see its orders — add missing pancake photos and
// edit AI descriptions here. This is the only place descriptions are editable.

import { useState, useEffect, useRef } from "react";
import { getSeason, seasonOptions } from "./utils/season";
import { db, storage } from "./firebase";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection, query, where, getDocs, doc, updateDoc, setDoc, deleteDoc,
  serverTimestamp, increment, orderBy, limit, Timestamp,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { ensureContainerShifts } from "./HistoricalUploadModal";
import { STAFF_THEME, FONT } from "./theme/staff";
import ReissueTicketModal from "./components/ReissueTicketModal";
import { trashOrder, restoreOrder, deleteOrderForever, listTrashedOrders, daysUntilPurge, TRASH_RETENTION_DAYS } from "./utils/trash";

const BRAND = { ...STAFF_THEME,
};

export default function ShiftArchiveView({ user, navigate, embedded = false }) {
  // Warm up printer connection so Safari trusts it when we need to reprint
  useEffect(() => { fetch("http://192.168.1.69", { mode: "no-cors" }).catch(() => {}); }, []);

  const [shifts,        setShifts]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [expanded,      setExpanded]      = useState({});
  const [shiftOrders,   setShiftOrders]   = useState({});
  const [loadingOrders, setLoadingOrders] = useState({});
  const [error,         setError]         = useState(null);

  // Photo upload flow
  const [phase,         setPhase]         = useState("list");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [capturedBlob,  setCapturedBlob]  = useState(null);
  const [capturedUrl,   setCapturedUrl]   = useState(null);
  const [uploadPct,     setUploadPct]     = useState(0);

  // Framing state
  const [framingImg,    setFramingImg]    = useState(null);
  const [cSize,         setCSize]         = useState(() => window.innerWidth);
  const framingXformRef        = useRef({ scale: 1, tx: 0, ty: 0 });
  const [framingXform, setFramingXform]   = useState({ scale: 1, tx: 0, ty: 0 });
  const framingContainerRef    = useRef(null);
  const framingElRef           = useRef(null);
  const framingGestureStateRef = useRef(null);

  // Awaiting-photo counts per shift (for "needs attention" section)
  const [awaitingByShift, setAwaitingByShift] = useState({});

  // ── Trash bin state ───────────────────────────────────────────────────────
  const [trashedOrders, setTrashedOrders] = useState([]);
  const [trashExpanded, setTrashExpanded] = useState(false);
  const [trashBusy,     setTrashBusy]     = useState(false);

  async function refreshTrash() {
    try {
      const list = await listTrashedOrders();
      setTrashedOrders(list);
    } catch (e) {
      console.warn("Failed to load trash:", e);
    }
  }

  // Filters
  const [searchQuery,  setSearchQuery]  = useState("");
  const [filterArtist, setFilterArtist] = useState("");
  const [filterSeason, setFilterSeason] = useState("all");
  const [hideArchived, setHideArchived] = useState(false);
  const [sortDir,      setSortDir]      = useState("desc");

  // Description edit
  const [editingId,  setEditingId]  = useState(null);
  const [editDesc,   setEditDesc]   = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Bulk delete
  const [selectMode,      setSelectMode]      = useState(false);
  const [selectedIds,     setSelectedIds]     = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting,        setDeleting]        = useState(false);
  const [deleteError,     setDeleteError]     = useState(null);

  // Edit shift modal
  const [editingShift, setEditingShift] = useState(null);

  // Reference photo lightbox
  const [viewingRefOrder, setViewingRefOrder] = useState(null);

  // Re-issue modal
  const [reissueOrder, setReissueOrder]     = useState(null);

  // Recap share modal — shows public URL + regenerate trigger.
  const [recapShift, setRecapShift] = useState(null);

  const fileInputRef    = useRef(null);
  const pendingOrderRef = useRef(null);

  // ── LOAD SHIFTS ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    // Run the orphan/historical migration BEFORE loading shifts so any new
    // container shifts are included in the first query.
    ensureContainerShifts()
      .catch((err) => console.warn("Container sweep failed (non-fatal):", err.message))
      .then(() => getDocs(query(
        collection(db, "shift_summaries"),
        orderBy("closedAt", "desc"),
        limit(50)
      )))
      .then((snap) => {
        if (cancelled || !snap) return;
        setShifts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── DEEP-LINK: ?shiftId= auto-expand + scroll ──────────────────────────────
  // Populated by the CRM booking-detail "View shift →" button (and future
  // surfaces). Runs once after shifts load; a ref guards against re-firing
  // on any subsequent setShifts() (restore, trash, etc.).
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (loading || deepLinkHandledRef.current || shifts.length === 0) return;
    const sp = new URLSearchParams(window.location.search);
    const targetId = sp.get("shiftId");
    if (!targetId) return;
    if (!shifts.some((s) => s.id === targetId)) return; // not in loaded set
    deepLinkHandledRef.current = true;
    setExpanded((p) => ({ ...p, [targetId]: true }));
    loadOrders(targetId);
    // Let React commit the expand + orders list, then scroll.
    setTimeout(() => {
      document.getElementById(`shift-row-${targetId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  }, [loading, shifts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load awaiting-photo counts so we can surface shifts needing attention
  useEffect(() => {
    getDocs(query(collection(db, "orders"), where("state", "==", "awaiting_photo")))
      .then(snap => {
        const counts = {};
        snap.docs.forEach(d => {
          const sid = d.data().shiftId;
          if (sid) counts[sid] = (counts[sid] ?? 0) + 1;
        });
        // Don't count trashed orders as needing attention — they're scheduled
        // for deletion, not awaiting work.
        snap.docs.forEach(d => {
          if (d.data().trashedAt) {
            const sid = d.data().shiftId;
            if (sid) counts[sid] = Math.max(0, (counts[sid] ?? 0) - 1);
          }
        });
        setAwaitingByShift(counts);
      })
      .catch(() => {});
  }, []);

  // Load the trash bin on mount and whenever an order is trashed / restored
  // / purged so the accordion header count stays fresh.
  useEffect(() => { refreshTrash(); }, []);

  // ── FRAMING: measure container ─────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "framing") return;
    const el = framingContainerRef.current;
    if (!el) return;
    const s = Math.min(el.offsetWidth, el.offsetHeight);
    if (s > 0) setCSize(s);
  }, [phase, framingImg]);

  // ── FRAMING: touch gestures ────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "framing" || cSize === 0) return;
    const el = framingContainerRef.current;
    if (!el) return;

    function getDist(t1, t2) { return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY); }
    function getMid(t1, t2)  { return { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 }; }

    function applyXform(next) {
      framingXformRef.current = next;
      if (framingElRef.current)
        framingElRef.current.style.transform = `translate(${next.tx}px, ${next.ty}px) scale(${next.scale})`;
    }

    function onTouchStart(e) {
      e.preventDefault();
      const cur  = framingXformRef.current;
      const rect = el.getBoundingClientRect();
      const cx   = rect.left + rect.width  / 2;
      const cy   = rect.top  + rect.height / 2;
      if (e.touches.length >= 2) {
        framingGestureStateRef.current = {
          type: "pinch", startDist: getDist(e.touches[0], e.touches[1]),
          startMid: getMid(e.touches[0], e.touches[1]), startXform: { ...cur }, cx, cy,
        };
      } else {
        framingGestureStateRef.current = {
          type: "pan", startX: e.touches[0].clientX, startY: e.touches[0].clientY, startXform: { ...cur },
        };
      }
    }

    function onTouchMove(e) {
      const g = framingGestureStateRef.current;
      if (!g) return;
      e.preventDefault();
      if (g.type === "pinch" && e.touches.length >= 2) {
        const newDist  = getDist(e.touches[0], e.touches[1]);
        const newMid   = getMid(e.touches[0],  e.touches[1]);
        const rawScale = g.startXform.scale * (newDist / g.startDist);
        const newScale = Math.max(0.1, Math.min(15, rawScale));
        const r        = newScale / g.startXform.scale;
        applyXform({
          scale: newScale,
          tx: g.startXform.tx * r + (g.startMid.x - g.cx) * (1 - r) + (newMid.x - g.startMid.x),
          ty: g.startXform.ty * r + (g.startMid.y - g.cy) * (1 - r) + (newMid.y - g.startMid.y),
        });
      } else if (g.type === "pan" && e.touches.length === 1) {
        applyXform({
          ...g.startXform,
          tx: g.startXform.tx + (e.touches[0].clientX - g.startX),
          ty: g.startXform.ty + (e.touches[0].clientY - g.startY),
        });
      }
    }

    function onTouchEnd() {
      framingGestureStateRef.current = null;
      setFramingXform({ ...framingXformRef.current });
    }

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
    };
  }, [phase, cSize]);

  // ── RETIRE SHIFT ──────────────────────────────────────────────────────────
  async function retireShift(shiftId) {
    await setDoc(doc(db, "shift_summaries", shiftId), { photosRetired: true }, { merge: true });
    setShifts(prev => {
      const exists = prev.find(s => s.id === shiftId);
      if (exists) return prev.map(s => s.id === shiftId ? { ...s, photosRetired: true } : s);
      return [...prev, { id: shiftId, photosRetired: true }];
    });
  }

  // ── EDIT SHIFT — name + dates ─────────────────────────────────────────────
  async function saveShiftEdit(shiftId, patch) {
    try {
      await updateDoc(doc(db, "shifts", shiftId), patch);
      // Mirror eventName onto shift_summaries if present (used by gallery)
      if (patch.eventName !== undefined) {
        await setDoc(doc(db, "shift_summaries", shiftId), { eventName: patch.eventName }, { merge: true }).catch(() => {});
      }
      setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, ...patch } : s));
      setEditingShift(null);
    } catch (err) {
      alert("Save failed: " + err.message);
    }
  }

  // ── HIDDEN ORDER TOGGLE — surface in /archive (mirror of GalleryEditPanel) ─
  async function toggleOrderHidden(order) {
    const next = !order.hidden;
    try {
      await updateDoc(doc(db, "orders", order.id), { hidden: next });
      if (order.shiftId) {
        setShiftOrders(prev => {
          if (!prev[order.shiftId]) return prev;
          return { ...prev, [order.shiftId]: prev[order.shiftId].map(o => o.id === order.id ? { ...o, hidden: next } : o) };
        });
      }
    } catch (err) {
      alert("Toggle failed: " + err.message);
    }
  }

  // ── TRASH / RESTORE / PURGE ───────────────────────────────────────────────
  // Soft-delete: move an order to trash. Recoverable within TRASH_RETENTION_DAYS
  // from the Trash accordion at the top of this view. A scheduled Cloud
  // Function (cleanupTrashedOrders) purges + emails Dan after the window.
  async function trashOrderClick(order) {
    const name = order.subjectName || order.recipientName || order.customerName || "this pancake";
    if (!window.confirm(`Move "${name}"${order.queueNumber ? ` (#${order.queueNumber})` : ""} to trash? You can restore it from Library → Shifts → Trash within ${TRASH_RETENTION_DAYS} days.`)) return;
    try {
      await trashOrder(order.id, user?.email);
      // Remove from the in-memory shift list so the UI updates immediately
      if (order.shiftId) {
        setShiftOrders(prev => {
          if (!prev[order.shiftId]) return prev;
          return { ...prev, [order.shiftId]: prev[order.shiftId].filter(o => o.id !== order.id) };
        });
      }
      await refreshTrash();
    } catch (err) {
      alert("Move to trash failed: " + err.message);
    }
  }

  // Restore a trashed order. Writes trashedAt/trashedBy back to null.
  async function restoreOrderClick(order) {
    setTrashBusy(true);
    try {
      await restoreOrder(order.id);
      await refreshTrash();
      // Also re-insert into the in-memory shift list if the shift is expanded
      if (order.shiftId && shiftOrders[order.shiftId]) {
        setShiftOrders(prev => ({
          ...prev,
          [order.shiftId]: [...prev[order.shiftId], { ...order, trashedAt: null, trashedBy: null }],
        }));
      }
    } catch (err) {
      alert("Restore failed: " + err.message);
    } finally {
      setTrashBusy(false);
    }
  }

  // Permanent delete from inside the Trash accordion. Strong confirmation.
  async function deleteForeverClick(order) {
    const name = order.subjectName || order.recipientName || order.customerName || "this pancake";
    if (!window.confirm(`Permanently delete "${name}"${order.queueNumber ? ` (#${order.queueNumber})` : ""}? Its photo + order record + token will be removed. This cannot be undone.`)) return;
    setTrashBusy(true);
    try {
      await deleteOrderForever(order);
      await refreshTrash();
    } catch (err) {
      alert("Delete failed: " + err.message);
    } finally {
      setTrashBusy(false);
    }
  }

  // ── BULK DELETE ───────────────────────────────────────────────────────────
  async function bulkDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      for (const shiftId of selectedIds) {
        // Load all orders for this shift
        const ordersSnap = await getDocs(query(
          collection(db, "orders"), where("shiftId", "==", shiftId)
        ));
        const orders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Best-effort Storage cleanup
        const storageUrls = orders.flatMap(o =>
          [o.photo_url, o.photo_cutout_url, o.imageUrl, o.croppedImageUrl].filter(Boolean)
        );
        await Promise.allSettled(
          storageUrls.map(url => deleteObject(storageRef(storage, url)).catch(() => {}))
        );

        // Delete orders
        await Promise.all(orders.map(o => deleteDoc(doc(db, "orders", o.id))));

        // Delete tokens referenced by those orders
        const tokenIds = [...new Set(orders.map(o => o.tokenId).filter(Boolean))];
        await Promise.all(tokenIds.map(tid => deleteDoc(doc(db, "tokens", tid))));

        // Delete shift + summary docs (catch individually — old shifts may lack one)
        await Promise.allSettled([
          deleteDoc(doc(db, "shifts", shiftId)),
          deleteDoc(doc(db, "shift_summaries", shiftId)),
        ]);
      }

      const gone = new Set(selectedIds);
      setShifts(prev => prev.filter(s => !gone.has(s.id)));
      setShiftOrders(prev => { const n = { ...prev }; gone.forEach(id => delete n[id]); return n; });
      setAwaitingByShift(prev => { const n = { ...prev }; gone.forEach(id => delete n[id]); return n; });
      setSelectedIds(new Set());
      setSelectMode(false);
      setShowDeleteModal(false);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  // ── SHIFT EXPAND / LOAD ORDERS ─────────────────────────────────────────────
  async function loadOrders(shiftId) {
    if (shiftOrders[shiftId] !== undefined || loadingOrders[shiftId]) return;
    setLoadingOrders(p => ({ ...p, [shiftId]: true }));
    try {
      const snap = await getDocs(query(
        collection(db, "orders"),
        where("shiftId", "==", shiftId)
      ));
      const orders = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => (o.state === "awaiting_photo" || o.state === "done") && !o.trashedAt)
        .sort((a, b) => (a.queueNumber ?? 0) - (b.queueNumber ?? 0));
      setShiftOrders(p => ({ ...p, [shiftId]: orders }));
    } catch { /* ignore */ }
    finally { setLoadingOrders(p => ({ ...p, [shiftId]: false })); }
  }

  function toggleShift(shiftId) {
    setExpanded(p => {
      const next = !p[shiftId];
      if (next) loadOrders(shiftId);
      return { ...p, [shiftId]: next };
    });
  }

  // ── PHOTO PICKER ───────────────────────────────────────────────────────────
  function openPhotoPicker(order) {
    pendingOrderRef.current = order;
    fileInputRef.current?.click();
  }

  function handleFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file || !pendingOrderRef.current) return;
    e.target.value = "";
    setSelectedOrder(pendingOrderRef.current);
    const url = URL.createObjectURL(file);
    setCapturedUrl(url);
    setCapturedBlob(file);
    const img = new window.Image();
    img.onload = () => {
      setFramingImg(img);
      framingXformRef.current = { scale: 1, tx: 0, ty: 0 };
      setFramingXform({ scale: 1, tx: 0, ty: 0 });
    };
    img.src = url;
    setError(null);
    setPhase("framing");
  }

  function cropAndPreview() {
    if (!framingImg || cSize === 0) return;
    const iw = framingImg.naturalWidth, ih = framingImg.naturalHeight;
    const baseScale  = Math.min(cSize / iw, cSize / ih);
    const totalScale = baseScale * framingXformRef.current.scale;
    const { tx, ty } = framingXformRef.current;

    const srcX    = iw / 2 - (cSize / 2 + tx) / totalScale;
    const srcY    = ih / 2 - (cSize / 2 + ty) / totalScale;
    const srcSize = cSize / totalScale;

    const outputSize = Math.min(Math.round(Math.max(srcSize, 100)), 1920);
    const canvas     = document.createElement("canvas");
    canvas.width = outputSize; canvas.height = outputSize;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, outputSize, outputSize);

    const clampedX = Math.max(0, srcX);
    const clampedY = Math.max(0, srcY);
    const clampedW = Math.min(iw, srcX + srcSize) - clampedX;
    const clampedH = Math.min(ih, srcY + srcSize) - clampedY;

    if (clampedW > 0 && clampedH > 0) {
      ctx.drawImage(
        framingImg,
        clampedX, clampedY, clampedW, clampedH,
        (clampedX - srcX) / srcSize * outputSize,
        (clampedY - srcY) / srcSize * outputSize,
        clampedW / srcSize * outputSize,
        clampedH / srcSize * outputSize,
      );
    }

    canvas.toBlob(blob => {
      setCapturedBlob(blob);
      setCapturedUrl(URL.createObjectURL(blob));
      setPhase("preview");
    }, "image/jpeg", 0.92);
  }

  async function upload() {
    if (!capturedBlob || !selectedOrder) return;
    setPhase("uploading");
    setUploadPct(10);
    setError(null);
    try {
      const tokenId = selectedOrder.tokenId ?? selectedOrder.id;
      const orderId = selectedOrder.id;
      const ts      = Date.now();

      const origRef = storageRef(storage, `orders/${tokenId}/pancake_original_${ts}.jpg`);
      await uploadBytes(origRef, capturedBlob, { contentType: "image/jpeg" });
      const photoUrl = await getDownloadURL(origRef);
      setUploadPct(60);

      await updateDoc(doc(db, "orders", orderId), {
        photo_url: photoUrl, state: "done", photographedAt: serverTimestamp(),
      });
      setUploadPct(85);

      if (selectedOrder.shiftId) {
        updateDoc(doc(db, "shifts", selectedOrder.shiftId), { awaitingPhotoCount: increment(-1) }).catch(() => {});
      }

      try {
        httpsCallable(getFunctions(getApp()), "generatePancakeDescription")({
          orderId, photoUrl, tokenId,
          subjectName:   selectedOrder.subjectName   || null,
          recipientName: selectedOrder.recipientName || selectedOrder.customerName || null,
          notes:         selectedOrder.notes         || null,
        });
      } catch { /* non-fatal */ }

      // Update local order list so the photo appears without re-querying
      setShiftOrders(p => {
        const sid = selectedOrder.shiftId;
        if (!p[sid]) return p;
        return { ...p, [sid]: p[sid].map(o => o.id === orderId ? { ...o, photo_url: photoUrl, state: "done" } : o) };
      });

      setUploadPct(100);
      setPhase("complete");
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.message);
      setPhase("preview");
    }
  }

  function backToList() {
    setCapturedBlob(null); setCapturedUrl(null); setUploadPct(0);
    setSelectedOrder(null); setError(null);
    setFramingImg(null); pendingOrderRef.current = null;
    framingXformRef.current = { scale: 1, tx: 0, ty: 0 };
    setFramingXform({ scale: 1, tx: 0, ty: 0 });
    setPhase("list");
  }

  // ── DESCRIPTION EDIT ──────────────────────────────────────────────────────
  async function saveDesc(order) {
    setEditSaving(true);
    try {
      const trimmed = editDesc.trim();
      await updateDoc(doc(db, "orders", order.id), { ai_description: trimmed });
      setShiftOrders(p => {
        const sid = order.shiftId;
        if (!p[sid]) return p;
        return { ...p, [sid]: p[sid].map(o => o.id === order.id ? { ...o, ai_description: trimmed } : o) };
      });
      setEditingId(null);
    } catch { /* ignore */ }
    finally { setEditSaving(false); }
  }

  // ── FRAMING PHASE ─────────────────────────────────────────────────────────
  if (phase === "framing") {
    const iw        = framingImg?.naturalWidth  ?? 1;
    const ih        = framingImg?.naturalHeight ?? 1;
    const baseScale = cSize > 0 ? Math.min(cSize / iw, cSize / ih) : 1;
    const renderedW = Math.round(iw * baseScale);
    const renderedH = Math.round(ih * baseScale);
    const name      = selectedOrder?.subjectName || selectedOrder?.recipientName || selectedOrder?.customerName;
    return (
      <div style={{ position: "fixed", inset: 0, background: "#000", display: "flex", flexDirection: "column", touchAction: "none", fontFamily: FONT.body }}>
        <div style={{ padding: "0.6rem 1rem", background: "#0a1230", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, borderBottom: `1px solid rgba(74,164,225,0.12)` }}>
          <button onClick={backToList} style={hud}>Cancel</button>
          <span style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.9rem" }}>#{selectedOrder?.queueNumber} · {name}</span>
          <button onClick={cropAndPreview} style={{ background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.45rem 1rem", fontWeight: "bold", fontSize: "0.9rem", cursor: "pointer" }}>Use This ›</button>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#000" }}>
          <div ref={framingContainerRef} style={{ width: "100%", aspectRatio: "1 / 1", position: "relative", overflow: "hidden", touchAction: "none" }}>
            {cSize > 0 && framingImg && (
              <div ref={framingElRef} style={{ position: "absolute", width: renderedW, height: renderedH, left: (cSize - renderedW) / 2, top: (cSize - renderedH) / 2, transform: `translate(${framingXform.tx}px, ${framingXform.ty}px) scale(${framingXform.scale})`, transformOrigin: "center center", willChange: "transform" }}>
                <img src={capturedUrl} alt="" style={{ width: "100%", height: "100%", display: "block", userSelect: "none" }} />
              </div>
            )}
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
              <path fillRule="evenodd" d="M 0 0 H 100 V 100 H 0 Z M 50 4 A 46 46 0 1 0 50 96 A 46 46 0 1 0 50 4 Z" fill="rgba(0,0,0,0.52)" />
              <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(252,179,31,0.75)" strokeWidth="0.9" />
              <line x1="33" y1="4"  x2="33" y2="96" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
              <line x1="67" y1="4"  x2="67" y2="96" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
              <line x1="4"  y1="33" x2="96" y2="33" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
              <line x1="4"  y1="67" x2="96" y2="67" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" />
            </svg>
          </div>
        </div>
        <div style={{ padding: "0.65rem 1rem", background: "#0a1230", borderTop: `1px solid rgba(74,164,225,0.12)`, flexShrink: 0, textAlign: "center" }}>
          <p style={{ color: BRAND.muted, fontSize: "0.78rem", margin: 0 }}>Pinch to zoom · drag to reposition · frame the pancake in the circle</p>
        </div>
      </div>
    );
  }

  // ── PREVIEW PHASE ─────────────────────────────────────────────────────────
  if (phase === "preview") {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#000", display: "flex", flexDirection: "column", fontFamily: FONT.body }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {capturedUrl && <img src={capturedUrl} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
        </div>
        {error && <div style={{ background: "#300", color: "#f88", padding: "0.5rem 1rem", fontSize: "0.85rem", textAlign: "center" }}>{error}</div>}
        <div style={{ display: "flex", gap: "0.75rem", padding: "1.25rem", background: "#0a1230", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
          <button onClick={() => { setCapturedBlob(null); setCapturedUrl(null); setFramingImg(null); framingXformRef.current = { scale: 1, tx: 0, ty: 0 }; setFramingXform({ scale: 1, tx: 0, ty: 0 }); fileInputRef.current?.click(); }} style={btn(BRAND.card, "rgba(255,255,255,0.55)")}>Pick Different</button>
          <button onClick={upload} style={{ ...btn(BRAND.green, "white"), flex: 1 }}>Upload & Done ✓</button>
        </div>
      </div>
    );
  }

  // ── UPLOADING PHASE ───────────────────────────────────────────────────────
  if (phase === "uploading") {
    return (
      <div style={{ position: "fixed", inset: 0, background: BRAND.dark, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem", fontFamily: FONT.body }}>
        <div style={{ color: "white", fontSize: "1rem" }}>Uploading…</div>
        <div style={{ width: "240px", height: "6px", background: BRAND.card, borderRadius: "3px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${uploadPct}%`, background: BRAND.gold, borderRadius: "3px", transition: "width 0.4s ease" }} />
        </div>
      </div>
    );
  }

  // ── COMPLETE PHASE ────────────────────────────────────────────────────────
  if (phase === "complete") {
    const name = selectedOrder?.subjectName || selectedOrder?.recipientName || selectedOrder?.customerName;
    return (
      <div style={{ position: "fixed", inset: 0, background: BRAND.dark, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1.25rem", fontFamily: FONT.body, padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "3rem" }}>🥞</div>
        <div style={{ color: BRAND.gold, fontSize: "1.4rem", fontWeight: "bold" }}>Photo saved!</div>
        <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem" }}>#{selectedOrder?.queueNumber} · {name}</div>
        <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", maxWidth: "260px", lineHeight: 1.5 }}>AI description + cutout generating in background — check back in ~30 seconds.</div>
        <button onClick={backToList} style={{ ...btn(BRAND.gold, BRAND.dark), marginTop: "0.5rem", minWidth: "180px" }}>← Back to Archive</button>
      </div>
    );
  }

  // ── NEEDS ATTENTION: unarchived shifts with awaiting-photo orders ──────────
  const needsAttention = Object.keys(awaitingByShift)
    .map(id => shifts.find(s => s.id === id) ?? { id, eventName: null, closedAt: null })
    .filter(s => !s.photosRetired)
    .sort((a, b) => (a.closedAt?.toMillis?.() ?? 0) - (b.closedAt?.toMillis?.() ?? 0)); // oldest first — most urgent

  // ── FILTER / SORT DERIVED STATE ───────────────────────────────────────────
  const allSeasons = seasonOptions(shifts, s => s.closedAt);
  const allArtists = [...new Set(shifts.flatMap(s => Object.keys(s.artistBreakdown ?? {})))].sort();

  const hasActiveFilter = searchQuery || filterArtist || filterSeason !== "all" || hideArchived || sortDir === "asc";

  const displayedShifts = shifts
    .filter(s => {
      if (hideArchived && s.photosRetired) return false;
      if (searchQuery && !(s.eventName ?? "").toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterArtist && !Object.keys(s.artistBreakdown ?? {}).includes(filterArtist)) return false;
      if (filterSeason !== "all" && (!s.closedAt || getSeason(s.closedAt) !== filterSeason)) return false;
      return true;
    })
    .sort((a, b) => {
      const aMs = a.closedAt?.toMillis?.() ?? 0;
      const bMs = b.closedAt?.toMillis?.() ?? 0;
      return sortDir === "desc" ? bMs - aMs : aMs - bMs;
    });

  // ── LIST PHASE ────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileSelected} />

      {/* Header bar */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem", flexShrink: 0 }}>📋 Shift Archive</div>
        <div style={{ flex: 1 }} />
        {!selectMode ? (
          <>
            <button
              onClick={() => { setSelectMode(true); setSelectedIds(new Set()); }}
              style={hud}
              title="Select shifts to delete"
            >
              🗑 Delete shifts
            </button>
            {!embedded && <button onClick={() => navigate("/menu")} style={hud}>← Menu</button>}
          </>
        ) : (
          <>
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>Tap shift rows · {selectedIds.size} selected</span>
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={selectedIds.size === 0}
              style={{ ...hud, borderColor: selectedIds.size > 0 ? "#c0392b" : BRAND.border, color: selectedIds.size > 0 ? "#e74c3c" : BRAND.border, cursor: selectedIds.size > 0 ? "pointer" : "default", fontWeight: selectedIds.size > 0 ? 800 : 400 }}
            >
              🗑 Delete {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
            </button>
            <button onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }} style={hud}>Cancel</button>
          </>
        )}
      </div>

      {/* Edit shift modal */}
      {editingShift && (
        <EditShiftModal
          shift={editingShift}
          onClose={() => setEditingShift(null)}
          onSave={(patch) => saveShiftEdit(editingShift.id, patch)}
        />
      )}

      {/* Confirmation modal */}
      {showDeleteModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "1.5rem" }}>
          <div style={{ background: BRAND.panel, border: `1px solid #500`, borderRadius: "14px", padding: "1.75rem", maxWidth: "360px", width: "100%", fontFamily: FONT.body }}>
            <div style={{ color: "#e74c3c", fontWeight: "bold", fontSize: "1.1rem", marginBottom: "0.75rem" }}>Delete {selectedIds.size} shift{selectedIds.size > 1 ? "s" : ""}?</div>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.88rem", lineHeight: 1.6, margin: "0 0 1.25rem" }}>
              All orders, tickets, and photos for the selected shift{selectedIds.size > 1 ? "s" : ""} will be permanently deleted. This cannot be undone.
            </p>
            {deleteError && <div style={{ color: "#e74c3c", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleteError(null); }} disabled={deleting} style={{ ...hud, flex: 1, textAlign: "center" }}>Cancel</button>
              <button onClick={bulkDelete} disabled={deleting} style={{ flex: 2, background: deleting ? "#300" : "#c0392b", color: "white", border: "none", borderRadius: "8px", padding: "0.6rem", fontWeight: "bold", fontSize: "0.9rem", cursor: deleting ? "default" : "pointer" }}>
                {deleting ? "Deleting…" : "Delete Everything"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trash bin — appears above everything else. Only renders when
          there's something in trash. Shows countdown per item, includes
          Restore + Delete forever actions. Items auto-purge after
          TRASH_RETENTION_DAYS via cleanupTrashedOrders Cloud Function. */}
      {trashedOrders.length > 0 && (
        <TrashSection
          orders={trashedOrders}
          expanded={trashExpanded}
          setExpanded={setTrashExpanded}
          onRestore={restoreOrderClick}
          onDeleteForever={deleteForeverClick}
          busy={trashBusy}
        />
      )}

      {/* Needs Attention section */}
      {!loading && needsAttention.length > 0 && (
        <div style={{ borderBottom: `1px solid ${BRAND.divider}` }}>
          <div style={{ padding: "0.5rem 1rem 0.25rem", background: BRAND.dark }}>
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Needs attention — {needsAttention.length} shift{needsAttention.length > 1 ? "s" : ""} with orders waiting
            </span>
          </div>
          {needsAttention.map(shift => {
            const dateStr = (shift.openedAt ?? shift.closedAt)
              ? new Date((shift.openedAt ?? shift.closedAt).toMillis()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : null;
            const count = awaitingByShift[shift.id] ?? 0;
            return (
              <div key={shift.id} style={{ background: BRAND.card, padding: "0.5rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem", borderTop: `1px solid ${BRAND.divider}`, border: `1px solid ${BRAND.orange}33` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", fontWeight: "bold" }}>{shift.eventName ?? "Unnamed Shift"}</span>
                  {dateStr && <span style={{ color: BRAND.sky, fontSize: "0.72rem", marginLeft: "0.5rem" }}>{dateStr}</span>}
                  <span style={{ color: BRAND.gold, fontSize: "0.72rem", marginLeft: "0.5rem" }}>{count} waiting</span>
                </div>
                <button onClick={() => toggleShift(shift.id)} style={chip}>Open ↓</button>
                <button onClick={() => retireShift(shift.id)} style={chip} title="Removes from Needs Attention. Doesn't delete data.">✓ Photos done</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter bar */}
      {!loading && shifts.length > 0 && (
        <div style={{ padding: "0.6rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.divider}`, display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search event name…"
            style={filterInput}
          />
          {allArtists.length > 1 && (
            <select value={filterArtist} onChange={e => setFilterArtist(e.target.value)} style={filterInput}>
              <option value="">All artists</option>
              {allArtists.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}
          {allSeasons.length > 1 && (
            <select value={filterSeason} onChange={e => setFilterSeason(e.target.value)} style={filterInput}>
              <option value="all">All Seasons</option>
              {allSeasons.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <button onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")} style={chip}>
            {sortDir === "desc" ? "↓ Newest" : "↑ Oldest"}
          </button>
          <label style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", display: "flex", alignItems: "center", gap: "0.3rem", cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={hideArchived} onChange={e => setHideArchived(e.target.checked)} style={{ accentColor: BRAND.pink }} />
            Hide archived
          </label>
          {hasActiveFilter && (
            <button onClick={() => { setSearchQuery(""); setFilterArtist(""); setFilterSeason("all"); setHideArchived(false); setSortDir("desc"); }} style={{ ...chip, color: BRAND.gold, borderColor: "#3a3000" }}>
              Clear
            </button>
          )}
          <span style={{ color: BRAND.muted, fontSize: "0.72rem", marginLeft: "auto" }}>
            {displayedShifts.length} / {shifts.length}
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ color: "rgba(255,255,255,0.35)", padding: "3rem", textAlign: "center" }}>Loading…</div>
      ) : shifts.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.35)", padding: "3rem", textAlign: "center" }}>No shifts recorded yet.</div>
      ) : displayedShifts.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.35)", padding: "3rem", textAlign: "center" }}>No shifts match the current filters.</div>
      ) : (
        <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {displayedShifts.map(shift => {
            const isOpen    = expanded[shift.id] ?? false;
            const isRetired = shift.photosRetired === true;
            const orders    = shiftOrders[shift.id] ?? [];
            const isLoading = loadingOrders[shift.id] ?? false;
            // openedAt first: night shifts close after midnight and would label
            // a Saturday gig "Sunday" (older summaries lack openedAt → closedAt)
            const shiftDateTs = shift.openedAt ?? shift.closedAt;
            const dateStr   = shiftDateTs
              ? new Date(shiftDateTs.toMillis()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : null;

            const isSelected = selectedIds.has(shift.id);
            return (
              <div key={shift.id} id={`shift-row-${shift.id}`} style={{ background: BRAND.card, borderRadius: "10px", border: `1px solid ${isSelected ? "#600" : BRAND.border}`, overflow: "hidden", transition: "border-color 0.15s" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {selectMode && (
                    <label style={{ padding: "0 0 0 1rem", cursor: "pointer", display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => setSelectedIds(prev => { const n = new Set(prev); n.has(shift.id) ? n.delete(shift.id) : n.add(shift.id); return n; })}
                        style={{ width: "18px", height: "18px", accentColor: "#e74c3c", cursor: "pointer" }}
                      />
                    </label>
                  )}
                  <button
                    onClick={() => selectMode
                      ? setSelectedIds(prev => { const n = new Set(prev); n.has(shift.id) ? n.delete(shift.id) : n.add(shift.id); return n; })
                      : toggleShift(shift.id)
                    }
                    style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: "0.85rem 1rem", display: "flex", justifyContent: "space-between", alignItems: "center", textAlign: "left" }}
                  >
                    <div>
                      <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", fontWeight: "bold" }}>{shift.eventName ?? "Unnamed Shift"}</div>
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.2rem", alignItems: "center", flexWrap: "wrap" }}>
                        {dateStr           && <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem" }}>{dateStr}</span>}
                        {shift.totalOrders && <span style={{ color: BRAND.sky, fontSize: "0.72rem" }}>{shift.totalOrders} pancakes</span>}
                        {isRetired         && <span style={{ color: BRAND.sky, fontSize: "0.65rem", border: `1px solid ${BRAND.border}`, borderRadius: "4px", padding: "0.05rem 0.35rem" }}>archived</span>}
                      </div>
                    </div>
                    {!selectMode && <span style={{ color: BRAND.border, fontSize: "0.7rem", flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>}
                  </button>
                  {!selectMode && (
                    <div style={{ display: "flex", gap: "0.35rem", marginRight: "0.6rem", flexShrink: 0 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/library?tab=pancakes&shift=${shift.id}`); }}
                        title="Browse this shift's pancakes in the gallery"
                        style={{ background: "none", color: BRAND.sky, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.4rem 0.55rem", fontSize: "0.85rem", cursor: "pointer" }}
                      >
                        🖼
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setRecapShift(shift); }}
                        title="Generate / view the public recap page for this shift"
                        style={{ background: "none", color: BRAND.pink, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.4rem 0.55rem", fontSize: "0.85rem", cursor: "pointer" }}
                      >
                        🎁
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingShift(shift); }}
                        title="Edit shift name + dates"
                        style={{ background: "none", color: BRAND.gold, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.4rem 0.55rem", fontSize: "0.85rem", cursor: "pointer" }}
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div style={{ borderTop: `1px solid ${BRAND.divider}`, padding: "0.75rem", background: BRAND.panel }}>
                    {isLoading ? (
                      <div style={{ color: BRAND.sky, fontSize: "0.85rem", textAlign: "center", padding: "1rem" }}>Loading…</div>
                    ) : orders.length === 0 ? (
                      <div style={{ color: BRAND.sky, fontSize: "0.85rem", textAlign: "center", padding: "1rem" }}>No orders on record for this shift.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {orders.map(order => {
                          const name     = order.subjectName || order.recipientName || order.customerName;
                          const hasPhoto = !!order.photo_url;
                          const isEditing = editingId === order.id;

                          return (
                            <div key={order.id} style={{ background: BRAND.raised, borderRadius: "8px", padding: "0.75rem", border: `1px solid ${BRAND.border}` }}>
                              {/* Order header — two rows on narrow screens: identity on row 1, actions wrap onto row 2. */}
                              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                                {hasPhoto ? (
                                  <img src={order.thumbnailUrl || order.photo_url} alt={name} loading="lazy" decoding="async" width="48" height="48" style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `2px solid ${BRAND.border}` }} />
                                ) : order.croppedImageUrl ? (
                                  <img src={order.croppedImageUrl} alt={name} loading="lazy" decoding="async" width="48" height="48" style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `2px solid ${BRAND.border}` }} />
                                ) : (
                                  <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: BRAND.card, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `2px solid ${BRAND.border}`, color: "rgba(255,255,255,0.35)", fontSize: "1.2rem" }}>
                                    {order.orderType === "custom" ? "🎨" : "⚡"}
                                  </div>
                                )}
                                {/* Identity block — grows to fill the row; minWidth forces actions to wrap below on narrow screens rather than squish the name. */}
                                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                                  <div style={{ color: "white", fontWeight: "bold", fontSize: "0.88rem", lineHeight: 1.3, wordBreak: "break-word" }}>
                                    {order.queueNumber ? `#${order.queueNumber} · ` : ""}{name}
                                  </div>
                                  {order.subjectName && <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.74rem" }}>for {order.recipientName}</div>}
                                  {order.artistName  && <div style={{ color: BRAND.sky, fontSize: "0.72rem" }}>🎨 {order.artistName}</div>}
                                </div>
                                {/* Action cluster — wraps together as a unit; stays right-aligned on narrow. */}
                                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginLeft: "auto" }}>
                                  {(order.croppedImageUrl || order.imageUrl) && (
                                    <button onClick={() => setViewingRefOrder(order)} style={{ background: BRAND.card, color: BRAND.sky, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}>
                                      📷 Ref
                                    </button>
                                  )}
                                  {order.tokenId && (
                                    <button onClick={() => setReissueOrder(order)} style={{ background: BRAND.card, color: BRAND.gold, border: `1px solid rgba(252,179,31,0.3)`, borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}>
                                      🎟 Re-issue
                                    </button>
                                  )}
                                  {!hasPhoto && order.state === "awaiting_photo" && (
                                    <button onClick={() => openPhotoPicker(order)} style={{ background: "#1a2a1a", color: "#4caf50", border: "1px solid #2a4a2a", borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}>
                                      📷 Add Photo
                                    </button>
                                  )}
                                  <button
                                    onClick={() => toggleOrderHidden(order)}
                                    title={order.hidden ? "Currently hidden from gallery — tap to unhide" : "Hide from gallery (data preserved)"}
                                    style={{ background: order.hidden ? "rgba(155,89,182,0.2)" : "transparent", color: order.hidden ? "#c39bd3" : "rgba(255,255,255,0.45)", border: `1px solid ${order.hidden ? "#9b59b6" : BRAND.border}`, borderRadius: "8px", padding: "0.45rem 0.55rem", fontSize: "0.85rem", cursor: "pointer", flexShrink: 0 }}
                                  >
                                    {order.hidden ? "👁‍🗨" : "👁"}
                                  </button>
                                  {/* Visible spacer so 🗑 isn't mistaken as a pair with 👁. */}
                                  <div aria-hidden style={{ width: "1px", alignSelf: "stretch", background: BRAND.border, margin: "0 0.1rem" }} />
                                  <button
                                    onClick={() => trashOrderClick(order)}
                                    title={`Move to trash — recoverable for ${TRASH_RETENTION_DAYS} days`}
                                    style={{ background: "transparent", color: "rgba(255,255,255,0.45)", border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.45rem 0.55rem", fontSize: "0.85rem", cursor: "pointer", flexShrink: 0 }}
                                  >
                                    🗑
                                  </button>
                                </div>
                              </div>

                              {/* Description (only for orders with a photo) */}
                              {hasPhoto && (
                                <div style={{ marginTop: "0.6rem", paddingTop: "0.6rem", borderTop: `1px solid ${BRAND.divider}` }}>
                                  {isEditing ? (
                                    <div>
                                      <textarea
                                        value={editDesc}
                                        onChange={e => setEditDesc(e.target.value)}
                                        rows={4}
                                        style={{ width: "100%", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.82rem", padding: "0.5rem", boxSizing: "border-box", resize: "vertical", fontFamily: FONT.body, lineHeight: 1.5 }}
                                      />
                                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                                        <button onClick={() => setEditingId(null)} style={{ ...btn(BRAND.card, "rgba(255,255,255,0.55)"), flex: 1, fontSize: "0.8rem", padding: "0.45rem" }}>Cancel</button>
                                        <button onClick={() => saveDesc(order)} disabled={editSaving} style={{ ...btn(BRAND.gold, BRAND.dark), flex: 2, fontSize: "0.8rem", padding: "0.45rem" }}>
                                          {editSaving ? "Saving…" : "Save"}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                                      <p style={{ flex: 1, color: order.ai_description ? "rgba(255,255,255,0.55)" : BRAND.sky, fontSize: "0.82rem", lineHeight: 1.55, margin: 0, fontStyle: order.ai_description ? "normal" : "italic" }}>
                                        {order.ai_description ?? "No description yet — generating after photo upload, or add one manually."}
                                      </p>
                                      <button
                                        onClick={() => { setEditingId(order.id); setEditDesc(order.ai_description ?? ""); }}
                                        style={{ background: "none", border: `1px solid ${BRAND.border}`, color: "rgba(255,255,255,0.35)", borderRadius: "6px", padding: "0.25rem 0.55rem", fontSize: "0.72rem", cursor: "pointer", flexShrink: 0 }}
                                      >
                                        ✏️
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Reference photo lightbox ── */}
      {viewingRefOrder && (() => {
        const refSrc = viewingRefOrder.croppedImageUrl || viewingRefOrder.imageUrl;
        const refName = viewingRefOrder.subjectName || viewingRefOrder.recipientName || viewingRefOrder.customerName || "Reference";

        async function shareRef() {
          try {
            const resp = await fetch(refSrc);
            const blob = await resp.blob();
            const file = new File([blob], `${refName}-reference.jpg`, { type: blob.type });
            if (navigator.share && navigator.canShare?.({ files: [file] })) {
              await navigator.share({ files: [file], title: `${refName} — reference photo` });
            } else {
              // Fallback: download
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = file.name;
              a.click();
              URL.revokeObjectURL(a.href);
            }
          } catch { /* user cancelled share */ }
        }

        return (
          <div onClick={() => setViewingRefOrder(null)} style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(0,0,0,0.88)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "1rem", gap: "1rem",
          }}>
            <div style={{ color: "white", fontWeight: "bold", fontSize: "1rem", textAlign: "center" }}>
              #{viewingRefOrder.queueNumber} · {refName}
            </div>
            <img
              src={refSrc}
              alt={refName}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: "90vw", maxHeight: "70vh", borderRadius: "12px", objectFit: "contain" }}
            />
            <div style={{ display: "flex", gap: "0.75rem" }} onClick={e => e.stopPropagation()}>
              <button onClick={shareRef} style={{ background: BRAND.sky, color: "white", border: "none", borderRadius: "10px", padding: "0.7rem 1.5rem", fontSize: "0.9rem", fontWeight: "bold", cursor: "pointer" }}>
                Share / Save
              </button>
              <button onClick={() => setViewingRefOrder(null)} style={{ background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)", border: "none", borderRadius: "10px", padding: "0.7rem 1.5rem", fontSize: "0.9rem", cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Re-issue modal ── */}
      {reissueOrder && <ReissueTicketModal order={reissueOrder} onClose={() => setReissueOrder(null)} />}

      {/* ── Recap share modal ── */}
      {recapShift && <RecapShareModal shift={recapShift} onClose={() => setRecapShift(null)} />}
    </div>
  );
}

// ── RECAP SHARE MODAL ──────────────────────────────────────────────────────
// Per-shift recap controls: shows the public URL with copy/open, regenerate
// button, and a small status line for the last build. Calls
// triggerShiftRecapBuild on demand. URL works even before first build — page
// will show "not ready" until the function writes the doc.
export function RecapShareModal({ shift, onClose }) {
  const recapUrl = `${window.location.origin}/recap/${shift.id}`;
  const storyUrl = `${window.location.origin}/social-asset/shift-story/${shift.id}.jpg`;
  const [busy, setBusy]     = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState(null); // null | "built" | error msg
  const [storyBusy, setStoryBusy] = useState(false);
  const [storyErr, setStoryErr]   = useState(false); // recap doc / render not ready
  const [signoff, setSignoff]       = useState(shift.recapSignoff || "");
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved]   = useState(false);

  async function regenerate() {
    setBusy(true);
    setStatus(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "triggerShiftRecapBuild");
      const { data } = await fn({ shiftId: shift.id });
      setStatus(`✓ Built ${data.pancakeCount} pancake${data.pancakeCount === 1 ? "" : "s"}`);
    } catch (err) {
      setStatus(`✗ ${err.message || "Failed"}`);
    }
    setBusy(false);
  }

  // Save the operator sign-off onto the shift doc, then rebuild so the public
  // recap picks it up. Blank clears it → RecapPage falls back to its default.
  async function saveSignoff() {
    setSavingNote(true); setNoteSaved(false); setStatus(null);
    try {
      await updateDoc(doc(db, "shifts", shift.id), { recapSignoff: signoff.trim() || null });
      const fn = httpsCallable(getFunctions(getApp()), "triggerShiftRecapBuild");
      await fn({ shiftId: shift.id });
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2200);
    } catch (err) {
      setStatus(`✗ ${err.message || "Save failed"}`);
    }
    setSavingNote(false);
  }

  function copyUrl() {
    navigator.clipboard.writeText(recapUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  // Fetch the 9:16 Story JPEG and hand it to the native share sheet (iOS/PWA);
  // fall back to a download on desktop / unsupported browsers.
  async function shareStory() {
    setStoryBusy(true);
    try {
      const res = await fetch(storyUrl);
      if (!res.ok) throw new Error("not ready");
      const blob = await res.blob();
      const file = new File([blob], `recap-${shift.id}.jpg`, { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Shift recap", text: shift.eventName || "The Joy of Pancakes" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      }
    } catch (e) {
      if (e.name !== "AbortError") setStoryErr(true);
    }
    setStoryBusy(false);
  }

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 100, padding: "1rem",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: BRAND.panel, borderRadius: "14px",
        padding: "1.25rem", maxWidth: "440px", width: "100%",
        border: `1px solid ${BRAND.border}`,
        display: "flex", flexDirection: "column", gap: "0.85rem",
      }}>
        <div>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem", marginBottom: "0.2rem" }}>
            🎁 Event recap
          </div>
          <div style={{ color: BRAND.muted, fontSize: "0.82rem" }}>
            {shift.eventName || "Untitled shift"}
          </div>
        </div>

        <div style={{
          background: BRAND.card, border: `1px solid ${BRAND.border}`,
          borderRadius: "8px", padding: "0.55rem 0.7rem",
          color: BRAND.sky, fontSize: "0.78rem", wordBreak: "break-all",
          fontFamily: "monospace",
        }}>
          {recapUrl}
        </div>

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button onClick={copyUrl} style={{
            flex: 1, minWidth: "120px",
            background: copied ? BRAND.green : BRAND.card,
            color: copied ? "white" : BRAND.sky,
            border: `1px solid ${copied ? BRAND.green : BRAND.border}`,
            borderRadius: "8px", padding: "0.55rem", fontSize: "0.85rem",
            fontWeight: "bold", cursor: "pointer", fontFamily: FONT.body,
          }}>
            {copied ? "✓ Copied" : "📋 Copy link"}
          </button>
          <a href={recapUrl} target="_blank" rel="noreferrer" style={{
            flex: 1, minWidth: "120px",
            background: BRAND.card, color: BRAND.pink,
            border: `1px solid ${BRAND.border}`,
            borderRadius: "8px", padding: "0.55rem", fontSize: "0.85rem",
            fontWeight: "bold", cursor: "pointer", textAlign: "center",
            textDecoration: "none", fontFamily: FONT.body,
          }}>
            🔗 Open page
          </a>
        </div>

        {/* Instagram Story (9:16) image — generated from the recap doc */}
        <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: "0.85rem" }}>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.9rem", marginBottom: "0.5rem" }}>
            📲 Instagram Story
          </div>
          {storyErr ? (
            <div style={{ color: BRAND.dim, fontSize: "0.78rem", marginBottom: "0.5rem", lineHeight: 1.5 }}>
              Story image isn’t ready yet — tap “Rebuild recap” below, then reopen this.
            </div>
          ) : (
            <img
              src={storyUrl}
              alt="Shift recap story preview"
              onError={() => setStoryErr(true)}
              style={{ width: "100%", maxWidth: "220px", display: "block", margin: "0 auto 0.6rem", borderRadius: "10px", border: `1px solid ${BRAND.border}` }}
            />
          )}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button onClick={shareStory} disabled={storyBusy || storyErr} style={{
              flex: 1, minWidth: "120px",
              background: BRAND.gold, color: BRAND.dark, border: "none",
              borderRadius: "8px", padding: "0.55rem", fontSize: "0.85rem",
              fontWeight: "bold", cursor: storyBusy ? "wait" : "pointer",
              fontFamily: FONT.body, opacity: (storyBusy || storyErr) ? 0.6 : 1,
            }}>
              {storyBusy ? "Preparing…" : "📲 Share / Save"}
            </button>
            <a href={storyUrl} target="_blank" rel="noreferrer" style={{
              flex: 1, minWidth: "120px",
              background: BRAND.card, color: BRAND.pink,
              border: `1px solid ${BRAND.border}`,
              borderRadius: "8px", padding: "0.55rem", fontSize: "0.85rem",
              fontWeight: "bold", textAlign: "center", textDecoration: "none",
              fontFamily: FONT.body,
            }}>
              🔗 Open image
            </a>
          </div>
        </div>

        {/* Sign-off message — operator-customizable closing line on the recap */}
        <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: "0.85rem" }}>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "0.9rem", marginBottom: "0.35rem" }}>
            ✍️ Sign-off message
          </div>
          <div style={{ color: BRAND.dim, fontSize: "0.72rem", lineHeight: 1.5, marginBottom: "0.5rem" }}>
            Shown at the bottom of the recap. <b>First line = the big headline</b>; text below it is smaller.
            Leave blank for the default; personalize per event (e.g. add their name).
          </div>
          <textarea
            value={signoff}
            onChange={(e) => setSignoff(e.target.value)}
            rows={4}
            placeholder={"Every pancake here was made for you.\nThank you for having us. Share with your guests, and spread The Joy of Pancakes."}
            style={{
              width: "100%", resize: "vertical", boxSizing: "border-box",
              background: BRAND.card, border: `1px solid ${BRAND.border}`,
              borderRadius: "8px", padding: "0.55rem 0.7rem",
              color: BRAND.text, fontSize: "0.85rem", lineHeight: 1.5, fontFamily: FONT.body,
            }}
          />
          <button onClick={saveSignoff} disabled={savingNote} style={{
            marginTop: "0.5rem", width: "100%",
            background: noteSaved ? BRAND.green : BRAND.card,
            color: noteSaved ? "white" : BRAND.sky,
            border: `1px solid ${noteSaved ? BRAND.green : BRAND.border}`,
            borderRadius: "8px", padding: "0.55rem", fontSize: "0.85rem",
            fontWeight: "bold", cursor: savingNote ? "wait" : "pointer", fontFamily: FONT.body,
          }}>
            {savingNote ? "Saving…" : noteSaved ? "✓ Saved & rebuilt" : "💾 Save & rebuild"}
          </button>
        </div>

        <button onClick={regenerate} disabled={busy} style={{
          background: BRAND.gold, color: BRAND.dark,
          border: "none", borderRadius: "8px",
          padding: "0.65rem", fontSize: "0.88rem",
          fontWeight: "bold", cursor: busy ? "wait" : "pointer",
          fontFamily: FONT.body, opacity: busy ? 0.7 : 1,
        }}>
          {busy ? "Building…" : "🔄 Rebuild recap"}
        </button>

        {status && (
          <div style={{
            fontSize: "0.78rem", color: status.startsWith("✓") ? BRAND.green : "#ef537e",
            textAlign: "center",
          }}>
            {status}
          </div>
        )}

        <div style={{ color: BRAND.dim, fontSize: "0.72rem", lineHeight: 1.5 }}>
          The recap auto-builds when a shift closes. Rebuild after writing meditations
          or curating photos. Page is public — share with the client or attendees.
        </div>

        <button onClick={onClose} style={{
          background: "transparent", border: "none",
          color: BRAND.dim, fontSize: "0.82rem", cursor: "pointer",
          fontFamily: FONT.body,
        }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────
function btn(bg, color) {
  return { background: bg, color, border: "none", borderRadius: "12px", padding: "0.85rem 1.5rem", fontSize: "1rem", fontWeight: "bold", cursor: "pointer", fontFamily: FONT.body };
}
const hud        = { background: "transparent", border: "1px solid rgba(74,164,225,0.28)", color: "rgba(255,255,255,0.6)", borderRadius: "8px", padding: "0.4rem 0.75rem", fontSize: "0.8rem", cursor: "pointer" };
const filterInput = { background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "6px", color: "white", fontSize: "0.78rem", padding: "0.35rem 0.6rem", outline: "none", minWidth: "0", flex: "1 1 120px" };
const chip        = { background: "transparent", border: "1px solid rgba(74,164,225,0.28)", color: "rgba(255,255,255,0.55)", borderRadius: "6px", padding: "0.3rem 0.6rem", fontSize: "0.72rem", cursor: "pointer", whiteSpace: "nowrap" };

// ── EDIT SHIFT MODAL ────────────────────────────────────────────────────────
function EditShiftModal({ shift, onClose, onSave }) {
  const tsToInput = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const inputToTs = (s) => {
    if (!s) return null;
    const d = new Date(s + "T12:00:00");
    return isNaN(d) ? null : Timestamp.fromDate(d);
  };

  const [name,         setName]         = useState(shift.eventName ?? "");
  const [venueAddress, setVenueAddress] = useState(shift.venueAddress ?? "");
  const [venueIsPublic, setVenueIsPublic] = useState(shift.venueIsPublic !== false);
  const [started,      setStarted]      = useState(tsToInput(shift.startedAt));
  const [closed,       setClosed]       = useState(tsToInput(shift.closedAt));
  const [saving,       setSaving]       = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const patch = {};
    if (name.trim() !== (shift.eventName ?? "")) patch.eventName = name.trim() || null;
    if (venueAddress.trim() !== (shift.venueAddress ?? "")) patch.venueAddress = venueAddress.trim() || null;
    if (!!venueIsPublic !== (shift.venueIsPublic !== false)) patch.venueIsPublic = !!venueIsPublic;
    const sNew = inputToTs(started);
    const cNew = inputToTs(closed);
    const sOld = shift.startedAt?.toMillis?.() ?? null;
    const cOld = shift.closedAt?.toMillis?.() ?? null;
    if ((sNew?.toMillis() ?? null) !== sOld) patch.startedAt = sNew;
    if ((cNew?.toMillis() ?? null) !== cOld) patch.closedAt = cNew;
    if (Object.keys(patch).length === 0) { setSaving(false); onClose(); return; }
    await onSave(patch);
    setSaving(false);
  };

  const inputStyle = { width: "100%", background: BRAND.dark, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "6px", color: "white", padding: "0.5rem 0.65rem", fontSize: "0.9rem", boxSizing: "border-box", outline: "none", fontFamily: "inherit" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "1.5rem" }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} style={{ background: BRAND.panel, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "14px", padding: "1.4rem", maxWidth: "420px", width: "100%", fontFamily: FONT.body }}>
        <h2 style={{ margin: "0 0 0.9rem", color: "#fcb31f", fontSize: "1.05rem" }}>✏️ Edit shift</h2>
        <label style={{ display: "block", marginBottom: "0.7rem" }}>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", marginBottom: "0.25rem", textTransform: "uppercase" }}>Event name</div>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: "block", marginBottom: "0.4rem" }}>
          <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", marginBottom: "0.25rem", textTransform: "uppercase" }}>Venue address (optional)</div>
          <input type="text" value={venueAddress} onChange={(e) => setVenueAddress(e.target.value)} placeholder="4256 Magnolia Ave, St. Louis MO" style={inputStyle} />
        </label>
        {venueAddress.trim() && (
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(venueAddress.trim())}`}
            target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-block", color: "#4aa4e1", fontSize: "0.76rem", textDecoration: "none", fontWeight: 600, marginBottom: "0.55rem" }}
          >
            📍 Preview on Maps
          </a>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "0.9rem", cursor: venueAddress.trim() ? "pointer" : "default", opacity: venueAddress.trim() ? 1 : 0.55 }}>
          <input type="checkbox" checked={!venueIsPublic} onChange={(e) => setVenueIsPublic(!e.target.checked)} disabled={!venueAddress.trim()} style={{ accentColor: "#fcb31f" }} />
          <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.82rem" }}>Keep venue private (hide from keepsake + public site)</span>
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.55rem", marginBottom: "0.9rem" }}>
          <label>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", marginBottom: "0.25rem", textTransform: "uppercase" }}>Started</div>
            <input type="date" value={started} onChange={(e) => setStarted(e.target.value)} style={inputStyle} />
          </label>
          <label>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", marginBottom: "0.25rem", textTransform: "uppercase" }}>Closed</div>
            <input type="date" value={closed} onChange={(e) => setClosed(e.target.value)} style={inputStyle} />
          </label>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} disabled={saving} style={hud}>Cancel</button>
          <button type="submit" disabled={saving} style={{ background: "#fcb31f", color: BRAND.dark, border: "none", borderRadius: "8px", padding: "0.5rem 1rem", fontSize: "0.88rem", fontWeight: 800, cursor: saving ? "default" : "pointer" }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── TRASH SECTION ─────────────────────────────────────────────────────────────
// Collapsible accordion at the top of the Shifts list. Only visible when
// there are trashed orders. Each row shows thumbnail, name, when/by whom it
// was trashed, days until auto-purge (with ⚠ color when close), and
// Restore + Delete-forever buttons.
function TrashSection({ orders, expanded, setExpanded, onRestore, onDeleteForever, busy }) {
  const hasExpiringSoon = orders.some(o => {
    const d = daysUntilPurge(o);
    return d !== null && d <= 3;
  });
  const count = orders.length;
  return (
    <div style={{ borderBottom: `1px solid ${BRAND.divider}`, background: BRAND.panel }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "0.75rem",
          padding: "0.7rem 1rem", background: "none", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: "1rem" }}>🗑</span>
        <span style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.9rem" }}>Trash</span>
        <span style={{ color: BRAND.gold, fontSize: "0.78rem", fontWeight: 700 }}>{count}</span>
        {hasExpiringSoon && (
          <span style={{ background: "rgba(243,156,18,0.18)", border: "1px solid #f39c12", color: "#f39c12", fontSize: "0.68rem", fontWeight: 700, padding: "0.1rem 0.45rem", borderRadius: "999px" }}>
            ⚠ some expiring within 3 days
          </span>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
          Auto-purge after {TRASH_RETENTION_DAYS} days
        </span>
        <span style={{ color: BRAND.muted, fontSize: "0.8rem" }}>{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div style={{ padding: "0.5rem 1rem 1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {orders.map(o => {
            const name = o.subjectName || o.recipientName || o.customerName || "(unnamed)";
            const days = daysUntilPurge(o);
            const expiringSoon = days !== null && days <= 3;
            const trashedAt = o.trashedAt?.toDate?.().toLocaleDateString?.("en-US", { month: "short", day: "numeric" }) || "";
            const thumb = o.cutoutThumbnailUrl || o.thumbnailUrl || o.photo_cutout_url || o.photo_url;
            return (
              <div key={o.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.5rem", flexWrap: "wrap" }}>
                {thumb ? (
                  <img src={thumb} alt="" loading="lazy" width="40" height="40" style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover", flexShrink: 0, filter: "grayscale(0.4) brightness(0.7)" }} />
                ) : (
                  <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: BRAND.raised, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", color: BRAND.dim, flexShrink: 0 }}>🗑</div>
                )}
                <div style={{ flex: "1 1 140px", minWidth: 0 }}>
                  <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.queueNumber ? `#${o.queueNumber} · ` : ""}{name}
                  </div>
                  <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
                    trashed {trashedAt}{o.trashedBy ? ` · by ${o.trashedBy.split("@")[0]}` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{
                    color: expiringSoon ? "#f39c12" : BRAND.muted,
                    fontSize: "0.72rem", fontWeight: expiringSoon ? 800 : 600,
                  }}>
                    {days === 0 ? "purges today" : days === 1 ? "1 day left" : `${days} days left`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.4rem", flexShrink: 0 }}>
                  <button
                    onClick={() => onRestore(o)}
                    disabled={busy}
                    style={{ background: "rgba(112,190,68,0.18)", border: "1px solid #70be44", color: "#70be44", borderRadius: "8px", padding: "0.4rem 0.75rem", fontSize: "0.78rem", fontWeight: 700, cursor: busy ? "default" : "pointer" }}
                  >
                    ↺ Restore
                  </button>
                  <button
                    onClick={() => onDeleteForever(o)}
                    disabled={busy}
                    style={{ background: "rgba(192,57,43,0.18)", border: "1px solid rgba(192,57,43,0.5)", color: "#e74c3c", borderRadius: "8px", padding: "0.4rem 0.75rem", fontSize: "0.78rem", fontWeight: 700, cursor: busy ? "default" : "pointer" }}
                  >
                    Delete forever
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

